import React from "react";

import { FullpageSpinner } from "components";
import AppError, { AppErrorVariant } from "components/AppError";

interface Props {
	/** Provider name, for the log line and the staff details panel. */
	label: string;
	/**
	 * Whether to block on the request. Optional because not every provider
	 * gates rendering on its own load - Organizations and Projects render their
	 * children while fetching, and making them block would change what the
	 * login page does.
	 */
	loading?: boolean;
	error?: Error | undefined;
	/** use-http's response object, when the request produced one. */
	response?: { status?: number; headers?: Headers };
	onRetry?: () => void;
	/** Named in the "no such workspace" copy, when a 404 is the real answer. */
	subdomain?: string;
	children: React.ReactNode;
}

/**
 * Blocks its children until a provider's boot request has succeeded, and shows
 * a proper failure state when it hasn't.
 *
 * Four providers gate the app's boot - Tenant, Auth, Organizations, Projects -
 * and each used to render its own ad-hoc error markup. Sharing one gate keeps
 * them from drifting apart again, which is how three of them ended up printing
 * raw JSON at the user.
 */
const BootGate: React.FC<Props> = ({
	label,
	loading = false,
	error,
	response,
	onRetry,
	subdomain,
	children,
}) => {
	const status = response?.status;
	const failed = !!error || (!!status && status >= 400);

	React.useEffect(() => {
		if (!failed) return;

		logger.error(`[${label}] boot request failed`, {
			status,
			message: error?.message,
		});
	}, [failed, label, status, error]);

	if (loading) {
		return <FullpageSpinner />;
	}

	if (failed) {
		return (
			<AppError
				variant={variantFor(status)}
				source={label}
				status={status}
				subdomain={subdomain}
				onRetry={onRetry}
				retryAfterMs={retryAfterMs(response)}
			/>
		);
	}

	return <>{children}</>;
};

/**
 * No status at all means the request never reached us - a dead backend, a
 * dropped connection, DNS. Same user experience as a 503, so same variant.
 */
const variantFor = (status?: number): AppErrorVariant => {
	if (status === undefined) return "unavailable";
	if (status === 404) return "notFound";
	if (status === 503 || status === 502 || status === 504) return "unavailable";

	return "unexpected";
};

const retryAfterMs = (response?: Props["response"]): number | undefined => {
	const header = response?.headers?.get?.("Retry-After");
	if (!header) return undefined;

	// Only the delta-seconds form is worth handling; the HTTP-date form isn't
	// something our API sends.
	const seconds = Number(header);

	return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : undefined;
};

export default BootGate;
